import React, { useMemo, useRef, useState } from 'react';
import { Container } from '../../../components/commons';
import Table from '../../../components/Table';
import Pagination from '../../../components/Table/Pagination';
import { FiEdit, FiTrash2, FiPlay, FiList } from 'react-icons/fi';
import { theme } from '../../../styles/theme';
import { useApiIntegrations, useDeleteApiIntegration } from '../../../services/apiIntegrations.service';
import useToast from '../../../hooks/useToast';
import IntegrationModal, { ModalHandle } from './components/IntegrationModal';
import SyncModal, { SyncModalHandle } from './components/SyncModal';
import ApiDataModal, { ApiDataModalHandle } from './components/ApiDataModal';
import { ApiIntegration } from '../../../interfaces/ApiIntegrations';
import { createColumnHelper } from '@tanstack/react-table';

const ApiIntegrationPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const toast = useToast();
  const modalRef = useRef<ModalHandle>(null);
  const syncModalRef = useRef<SyncModalHandle>(null);
  const apiDataModalRef = useRef<ApiDataModalHandle>(null);

  const { data: apiIntegrations, isLoading } = useApiIntegrations(page);
  const deleteMutation = useDeleteApiIntegration();

  const handleCreate = () => {
    modalRef.current?.openFormModal();
  };

  const handleEdit = (integration: ApiIntegration) => {
    modalRef.current?.openFormModal(integration);
  };

  const handleSync = (integration: ApiIntegration) => {
    syncModalRef.current?.openSyncModal(integration);
  };

  const handleViewData = (integration: ApiIntegration) => {
    apiDataModalRef.current?.openModal(integration._id as string, integration.name);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta integração?')) {
      try {
        await deleteMutation.mutateAsync(id);
        toast.successToast('Integração excluída com sucesso!');
      } catch (err) {
        toast.errorToast('Erro ao excluir integração.');
      }
    }
  };

  const columnHelper = createColumnHelper<ApiIntegration>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => 'Nome',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('url', {
        header: () => 'URL',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('documentTypeMappings', {
        header: () => 'Tipos de Docs Vinculados',
        cell: (info) => {
          const mappings = info.getValue() as ApiIntegration['documentTypeMappings'];
          if (!mappings || mappings.length === 0) return 'Nenhum';
          return mappings.map(m => m.documentTypeName || m.documentType).join(', ');
        },
      }),
      columnHelper.accessor('_id', {
        header: () => 'Ações',
        cell: (info) => {
          const integration = info.row.original;
          return (
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => handleSync(integration)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: theme.colors.success }}
                title="Sincronizar"
              >
                <FiPlay size={18} />
              </button>
              <button
                onClick={() => handleViewData(integration)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: theme.colors.secondary }}
                title="Ver Dados Extraídos"
              >
                <FiList size={18} />
              </button>
              <button
                onClick={() => handleEdit(integration)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: theme.colors.primary }}
                title="Editar"
              >
                <FiEdit size={18} />
              </button>
              <button
                onClick={() => handleDelete(integration._id as string)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: theme.colors.danger }}
                title="Excluir"
              >
                <FiTrash2 size={18} />
              </button>
            </div>
          );
        },
      }),
    ],
    [columnHelper]
  );

  return (
    <Container>
      <IntegrationModal ref={modalRef} />
      <SyncModal ref={syncModalRef} />
      <ApiDataModal ref={apiDataModalRef} />

      <Table
        title='Integrações API'
        isLoading={isLoading}
        create={handleCreate}
        columns={columns}
        data={apiIntegrations?.data || []}
      />
      
      {apiIntegrations?.meta && (
        <Pagination
          totalPages={Math.ceil((apiIntegrations.meta.total || 0) / (apiIntegrations.meta.limit || 10))}
          page={page}
          perPage={apiIntegrations.meta.limit || 10}
          pageChangeHandler={setPage}
        />
      )}
    </Container>
  );
};

export default ApiIntegrationPage;
